import { Accordion, Box, Center, Flex, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useState } from 'react';
import SectionCard from 'src/cashflows/components/SectionCard';
import { SectionAddAction, SectionAddButton } from 'src/cashflows/components/SectionAddButton';
import { formatMoney, TenantCurrency } from 'src/common/currency';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FinancialRows } from './FinancialRowsTable';
import { FinancialRow, FinancialSubSection } from './financialRows';
import { AssetFeedBadges, AssetFeedIcons } from 'src/financial/financial-data/AssetFeedIndicators';
import { useCurrency } from 'src/common/hooks/useCurrency';

const CHEVRON_SIZE = 20;

type FinancialSectionTableProps = {
  title: string;
  rows: FinancialRow[];
  total: number;
  fullTotal?: number;
  totalLabel?: string;
  subSections?: FinancialSubSection[];
  onRowClick?: (row: FinancialRow) => void;
  addAction?: SectionAddAction;
  hideTotal?: boolean;
  showOwnership?: boolean;
  ownershipLabel?: string;
};

export function FinancialSectionTable({
  title,
  rows,
  total,
  fullTotal,
  totalLabel = 'Total',
  subSections,
  onRowClick,
  addAction,
  hideTotal = false,
  showOwnership = false,
  ownershipLabel,
}: FinancialSectionTableProps) {
  const currency = useCurrency();
  const [openValue, setOpenValue] = useState<string | null>(null);

  const isOpen = openValue === title;

  return (
    <SectionCard
      value={title}
      title={title}
      titleAdornment={<AssetFeedBadges items={rows} />}
      count={rows.length}
      showCount={false}
      rightSectionPlacement="overlay"
      openValue={openValue}
      onOpenChange={setOpenValue}
      styles={{
        // As in the Cashflows sections, the tint belongs to the item rather than the control, so
        // it sits behind the rows an open section reveals instead of stopping at the header.
        // `overflow` is what keeps it inside the corners once it reaches that far.
        item: {
          border: 'none',
          borderRadius: 20,
          backgroundColor: 'var(--mantine-color-gray-0)',
          overflow: 'hidden',
        },
        control: {
          backgroundColor: 'transparent',
          paddingLeft: 24,
          paddingRight: 24,
          paddingTop: 22,
          paddingBottom: 22,
        },
        chevron: { display: 'none' },
        label: { paddingTop: 0, paddingBottom: 0 },
        content: { paddingTop: 32, paddingBottom: 32, paddingLeft: 24, paddingRight: 24 },
      }}
      rightSection={
        <Group
          gap={24}
          wrap="nowrap"
          align="center"
          style={{ position: 'absolute', top: 0, bottom: 0, right: 24, pointerEvents: 'auto' }}
        >
          {!hideTotal && (
            <Stack gap={0} align="flex-end">
              <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.4">
                {totalLabel}:{' '}
                <Text span inherit c="gray.10">
                  {formatMoney(total, currency)}
                </Text>
              </Text>
              <FullTotalSubtext fullTotal={fullTotal} currency={currency} />
            </Stack>
          )}
          {addAction && <SectionAddButton action={addAction} ariaLabel={`Add ${title}`} />}
          <UnstyledButton
            onClick={() => setOpenValue(isOpen ? null : title)}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <IconChevronDown
              size={CHEVRON_SIZE}
              color="var(--mantine-color-black)"
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </UnstyledButton>
        </Group>
      }
    >
      {subSections && subSections.length > 0 ? (
        <FinancialSubSections
          sections={subSections}
          onRowClick={onRowClick}
          showOwnership={showOwnership}
          ownershipLabel={ownershipLabel}
        />
      ) : (
        <FinancialRows
          rows={rows}
          onRowClick={onRowClick}
          showOwnership={showOwnership}
          ownershipLabel={ownershipLabel}
        />
      )}
    </SectionCard>
  );
}

// A section holding a joint item does not add up to the figures the adviser entered, so the
// full total sits under the share total, the same way a row carries its whole value.
function FullTotalSubtext({
  fullTotal,
  currency,
}: {
  fullTotal?: number;
  currency: TenantCurrency;
}) {
  if (fullTotal == null) return null;
  return (
    <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.5">
      of {formatMoney(fullTotal, currency)}
    </Text>
  );
}

const SUBSECTION_ARROW_SIZE = 20;

function FinancialSubSections({
  sections,
  onRowClick,
  showOwnership = false,
  ownershipLabel,
}: {
  sections: FinancialSubSection[];
  onRowClick?: (row: FinancialRow) => void;
  showOwnership?: boolean;
  ownershipLabel?: string;
}) {
  const currency = useCurrency();
  const [openValues, setOpenValues] = useState<string[]>([]);

  return (
    <Accordion
      multiple
      chevron={null}
      value={openValues}
      onChange={setOpenValues}
      styles={{
        item: { border: 'none', background: 'transparent' },
        control: { backgroundColor: 'transparent', padding: '10px 0' },
        chevron: { display: 'none' },
        label: { padding: 0 },
        content: { padding: 0 },
      }}
    >
      {sections.map(section => {
        const isOpen = openValues.includes(section.key);

        return (
          <Accordion.Item role="region" key={section.key} value={section.key}>
            <Accordion.Control>
              <Flex justify="space-between" align="center">
                <Group gap={12} wrap="nowrap" align="center">
                  <Center
                    style={{
                      width: SUBSECTION_ARROW_SIZE,
                      height: SUBSECTION_ARROW_SIZE,
                      borderRadius: 4,
                      backgroundColor: 'var(--mantine-color-gray-2)',
                      flexShrink: 0,
                    }}
                  >
                    <IconChevronDown
                      size={14}
                      color="var(--mantine-color-black)"
                      style={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </Center>
                  <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
                    {section.title}
                  </Text>
                  <AssetFeedIcons items={section.rows} />
                </Group>
                <Stack gap={0} align="flex-end">
                  <Text size="sm" fw={FONT_WEIGHTS.bold} c="black">
                    {formatMoney(section.total, currency)}
                  </Text>
                  <FullTotalSubtext fullTotal={section.fullTotal} currency={currency} />
                </Stack>
              </Flex>
            </Accordion.Control>
            <Accordion.Panel>
              <Box
                ml={SUBSECTION_ARROW_SIZE / 2 - 1}
                pl={20}
                style={{ borderLeft: '1px solid var(--mantine-color-gray-2)' }}
              >
                <FinancialRows
                  rows={section.rows}
                  onRowClick={onRowClick}
                  showOwnership={showOwnership}
                  ownershipLabel={ownershipLabel}
                />
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}
